import { ColumnDef } from '@tanstack/react-table'
import {
  CalendarPlus as CalendarPlusIcon,
  ChatLines as ChatLinesIcon,
  DollarCircle,
  EditPencil as EditPencilIcon,
  PageEdit as PageEditIcon,
  PhoneIncome as PhoneIncomeIcon,
  PhoneOutcome as PhoneOutcomeIcon,
  Reply as ReplyIcon,
} from 'iconoir-react'
import { compact, startCase, uniqBy } from 'lodash'
import moment from 'moment'
import { DateComponent } from 'app/components/DateComponent'
import PhoneNumber from 'app/components/PhoneNumber'
import { LazyLocationCard } from 'app/components/shared/LazyLocationCard'
import { AnchorCell } from 'app/components/table/AnchorCell'
import { CampaignComboOption } from 'app/components/table/filters/CampaignComboOption'
import LinkedContactsCell from 'app/components/table/LinkedContactsCell'
import { StaffAssignCell } from 'app/components/table/StaffAssignCell'
import TagsCell from 'app/components/tags/TagsCell'
import {
  DateRangeOperator,
  FilterOperator,
  FilterValueType,
  FilterableColumn,
  PredefinedFilter,
  addDefaultFilterProperties,
} from 'app/filter.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { ActivityJson } from 'app/types/entities'
import type { AdCampaign } from 'app/types/entities'
import type { SearchResponse } from 'app/services/search'
import { getChannelColorMap } from 'lib/colorMappings'
import { Jsonify } from 'lib/toJSON'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { staffMembersFetcher } from 'app/lib/fetchers/staffMembersFetcher'
import { tagFilterOptionsFetcher } from 'app/lib/fetchers/tagFilterOptionsFetcher'
import { OptionType } from 'types/OptionType'
import { MONEY_ICON_FILL } from 'app/lib/iconColors'

export const getActivityTypeIcon = (activityType?: string, subType?: string) => {
  let iconClasses = { className: `h-full w-full`, color: `#FFFFFF` }
  switch (activityType) {
    case `FollowUp`:
      return <ReplyIcon {...iconClasses} />
    case `Call`:
      return subType === `outbound` ? <PhoneOutcomeIcon {...iconClasses} /> : <PhoneIncomeIcon {...iconClasses} />
    case `Chat`:
      return <ChatLinesIcon {...iconClasses} />
    case `Form`:
      return <PageEditIcon {...iconClasses} />
    case `Scheduler`:
      return <CalendarPlusIcon {...iconClasses} />
    default:
      return <EditPencilIcon {...iconClasses} />
  }
}

/** Column IDs to show for Voice Agent page per Figma design (in order) */
const VOICE_AGENT_COLUMN_IDS = [`activityDate`, `tags`, `contacts`, `locationId`, `owner`]

export function voiceAgentColumns(
  activityHandler: (row: ActivityJson) => void,
  onOwnerUpdate: (activityId: string, staffId?: string) => void,
  baseUrl?: string,
): ColumnDef<ActivityJson, any>[] {
  const allCols = defaultColumns(activityHandler, false, onOwnerUpdate, baseUrl)
  return compact(VOICE_AGENT_COLUMN_IDS.map((id) => allCols.find((col) => col.id === id)))
}

export const defaultColumns = (
  activityHandler,
  addMarketingColumns,
  onOwnerUpdate,
  baseUrl?: string,
  showUtmFields = true,
  onContactClick?: (contactId: string) => void,
): ColumnDef<ActivityJson, any>[] =>
  compact([
    {
      accessorFn: (row) => row.activityDate,
      id: `activityDate`,
      header: `Activity`,
      enableHiding: false,
      meta: {
        group: `General`,
        sortKey: `activityDate`,
        defaultVisible: true,
        pinned: true,
        ControlledComponent: (row) => (
          <AnchorCell
            href={`${baseUrl || ``}${baseUrl?.includes(`?`) ? `&` : `?`}drawer=${row?.id!}`}
            tooltipMessage={row?.type}
            clickHandler={() => activityHandler(row as ActivityJson)}
          >
            <div className="my-2 flex cursor-pointer items-center gap-x-2 align-middle">
              <div id="activity-record-icon" className="h-5 w-5 shrink-0 rounded-md bg-trn-700 p-1">
                {getActivityTypeIcon(row?.type, row?.subType)}
              </div>
              <div className="flex flex-col justify-between overflow-hidden overflow-ellipsis whitespace-nowrap">
                <span className="text-ui-xs">
                  {row?.type === `Scheduler`
                    ? row?.source === `In App Scheduler`
                      ? `In App Scheduler`
                      : `Online Scheduler`
                    : row?.type === `Call` && row?.subType
                      ? `${startCase(row.subType)} Call`
                      : startCase(row?.type)}
                </span>
                <DateComponent
                  value={row?.activityDate}
                  className="overflow-hidden overflow-ellipsis whitespace-nowrap text-body-xs text-subtle"
                />
              </div>
            </div>
          </AnchorCell>
        ),
      },
    },
    {
      accessorFn: (row) => row.contacts,
      id: `contacts`,
      header: `Linked Contacts`,
      meta: {
        defaultVisible: true,
        group: `General`,
        sortKey: `contactName.keyword`,
        ControlledComponent: (row) => (
          <LinkedContactsCell contacts={row?.contacts as any} onContactClick={onContactClick} />
        ),
      },
    },
    {
      id: `tags`,
      header: `Tags`,
      meta: {
        group: `General`,
        defaultVisible: true,
        ControlledComponent: (row) => {
          // Agent preview (test) calls are archived but visible to super users.
          // Show "Archived" first so it's immediately clear this isn't a real patient call.
          const tags = row?.archived ? [`Archived`, ...(row?.tags ?? []).filter((t) => t !== `Archived`)] : row?.tags
          return (
            <TagsCell
              tags={tags}
              workspaceId={row?.workspaceId}
              model="activities"
              modelId={row?.id!}
              activity={row?.type === `Call` ? row : undefined}
            />
          )
        },
      },
      enableSorting: false,
    },
    {
      accessorFn: (row) => row.locationId,
      id: `locationId`,
      header: `Location`,
      enableSorting: true,
      meta: {
        defaultVisible: true,
        group: `General`,
        sortKey: `locationId.keyword`,
        ControlledComponent: (row) => <LazyLocationCard locationId={row?.locationId} />,
      },
    },
    {
      accessorFn: (row) => row.owner,
      header: `Staff`,
      id: `owner`,
      meta: {
        group: `General`,
        defaultVisible: true,
        sortKey: `owner.name.keyword`,
        ControlledComponent: (row) => (
          <StaffAssignCell
            leadOwner={row?.owner}
            locationId={row?.locationId}
            workspaceId={row?.workspaceId}
            onChange={(val) => onOwnerUpdate(row?.id!, val?.value)}
            firstNameInitial
          />
        ),
      },
    },
    {
      accessorFn: (row) => row.vendor,
      id: `vendor`,
      header: `Vendor`,
      meta: {
        group: `General`,
        sortKey: `vendor.keyword`,
      },
    },
    {
      accessorFn: (row) => row.nameFromActivity,
      id: `nameFromActivity`,
      header: `Caller ID`,
      cell: (props) => <span className="capitalize">{props.getValue()?.toLocaleLowerCase() || ``}</span>,
      meta: {
        defaultVisible: true,
        group: `General`,
        sortKey: `nameFromActivity.keyword`,
      },
    },
    {
      accessorFn: (row) => row.phoneFromActivity,
      id: `phoneFromActivity`,
      header: `Phone Number`,
      cell: (props) => <PhoneNumber number={props.getValue()} />,
      meta: {
        defaultVisible: true,
        group: `General`,
        sortKey: `phoneFromActivity.keyword`,
      },
    },
    {
      accessorFn: (row) => row.subType,
      id: `formName`,
      header: `Form Name`,
      cell: (props) => {
        if (props.row.original.type !== `Form`) return null
        return props.getValue() || null
      },
      meta: {
        defaultVisible: false,
        group: `General`,
        sortKey: `subType.keyword`,
      },
    },
    {
      accessorFn: (row) => formatPhoneNumber(row.trackingNumber),
      id: `trackingNumber`,
      header: `Tracking Number`,
      meta: {
        defaultVisible: false,
        group: `General`,
        sortKey: `trackingNumber.keyword`,
      },
    },
    {
      accessorFn: (row) => row.trackingNumberName,
      id: `trackingNumberName`,
      header: `Tracking Number Name`,
      meta: {
        defaultVisible: false,
        group: `General`,
        sortKey: `trackingNumberName.keyword`,
      },
    },
    {
      accessorFn: (row) => row.channel,
      id: `channel`,
      header: `Channel`,
      cell: (props) => (
        <>
          <span
            className="mr-1.5 inline-block h-2 w-2 rounded-[2px] align-middle"
            style={{ backgroundColor: getChannelColorMap(props.getValue()) }}
          ></span>
          <span>{props.row.original.channel}</span>
        </>
      ),
      meta: {
        defaultVisible: true,
        group: `Marketing`,
        sortKey: `channel.keyword`,
      },
    },
    ...(addMarketingColumns
      ? [
          {
            accessorFn: (row) => row.utmSource,
            header: `Source`,
            id: `utmSource`,
            meta: {
              defaultVisible: false,
              group: `Marketing`,
              sortKey: `utmSource.keyword`,
              tooltipClassName: `lowercase`,
            },
          },
          {
            accessorFn: (row) => row.utmMedium,
            header: `Medium`,
            id: `utmMedium`,
            meta: {
              defaultVisible: false,
              group: `Marketing`,
              sortKey: `utmMedium.keyword`,
              tooltipClassName: `lowercase`,
            },
          },
          {
            id: `utmCampaign`,
            header: `Campaign`,
            accessorKey: `utmCampaign`,
            meta: {
              group: `Marketing`,
              defaultVisible: false,
              sortKey: `utmCampaign.keyword`,
              tooltipClassName: `lowercase`,
            },
          },
          {
            accessorFn: (row) => row.utmContent,
            id: `utmContent`,
            header: `Content`,
            enableSorting: false,
            meta: {
              defaultVisible: false,
              group: `Marketing`,
              sortKey: `utmContent.keyword`,
              tooltipClassName: `lowercase`,
            },
          },
          {
            accessorFn: (row) => row.utmKeywords,
            header: `Keyword`,
            id: `utmKeywords`,
            meta: {
              defaultVisible: false,
              group: `Marketing`,
              sortKey: `utmKeywords.keyword`,
              tooltipClassName: `lowercase`,
            },
          },
        ]
      : []),
    ...(showUtmFields
      ? [
          {
            accessorFn: (row) => row.gclid,
            id: `gclid`,
            header: `GCLID`,
            meta: {
              defaultVisible: false,
              group: `Marketing`,
              sortKey: `gclid.keyword`,
            },
          },
          {
            accessorFn: (row) => row.fbclid,
            id: `fbclid`,
            header: `FBCLID`,
            meta: {
              defaultVisible: false,
              group: `Marketing`,
              sortKey: `fbclid.keyword`,
            },
          },
          {
            accessorFn: (row) => row.referringUrl,
            id: `referringUrl`,
            header: `Referral URL`,
            meta: {
              defaultVisible: false,
              group: `Marketing`,
              sortKey: `referringUrl.keyword`,
              tooltipClassName: `lowercase`,
            },
          },
        ]
      : []),
  ])

export const getFilterableColumns = ({
  comActivityTypes,
  preAppliedFilters,
  addMarketingColumns,
  addFollowUp,
  showUtmFields = true,
}: {
  comActivityTypes: string[]
  preAppliedFilters?: PredefinedFilter
  addMarketingColumns: boolean
  addFollowUp: boolean
  showUtmFields?: boolean
}): FilterableColumn[] => {
  let filters = compact([
    {
      attribute: `activityDate`,
      displayName: `Activity Date`,
      type: FilterValueType.rangeDate,
      group: `General`,
      dateOperator: DateRangeOperator.this_month,
      defaultValue: [moment().startOf(`month`).format(`YYYY-MM-DD`), moment().endOf(`month`).format(`YYYY-MM-DD`)],
      operator: `between` as FilterOperator,
      includeToday: true,
    },
    {
      attribute: `type`,
      displayName: `Activity Type`,
      type: FilterValueType.multiple,
      group: `General`,
      options: compact([...comActivityTypes.filter((type) => type !== `Call`), `Inbound Call`, `Outbound Call`])
        .filter((type) => type !== `Scheduler` && type !== `Virtual Consultation`) // Scheduler should work with Online Scheduler & remove Virtual Consultation
        .filter((type) => addFollowUp || type !== `FollowUp`)
        ?.map((option) => ({
          id: option,
          name: startCase(option),
          value:
            option === `Online Scheduler`
              ? `Scheduler`
              : option === `Inbound Call`
                ? `Call:inbound`
                : option === `Outbound Call`
                  ? `Call:outbound`
                  : option,
        }))
        ?.sort((a, b) => a.value!.localeCompare(b.value!)),
    },
    {
      attribute: `tags`,
      displayName: `Tags`,
      type: FilterValueType.stringArray,
      group: `General`,
      fetcher: (params) => tagFilterOptionsFetcher(`Activity`, params),
    },
    {
      attribute: `owner.name`,
      displayName: `Staff`,
      type: FilterValueType.id,
      fetcher: (params) => staffMembersFetcher(params),
      queryByName: true,
      group: `General`,
    },
    {
      attribute: `locationId`,
      displayName: `Location`,
      type: FilterValueType.id,
      group: `General`,
      optionsSource: `/api/locations`,
      uniqOperator: `in`,
      standalone: true,
    } as FilterableColumn,
    {
      attribute: `nameFromActivity`,
      displayName: `Caller ID`,
      type: FilterValueType.string,
      group: `General`,
    },
    {
      attribute: `phoneFromActivity`,
      displayName: `Phone Number`,
      type: FilterValueType.string,
      group: `General`,
    },
    {
      attribute: `contactName`,
      displayName: `Linked Contacts`,
      type: FilterValueType.string,
      group: `General`,
    },
    {
      attribute: `vendor`,
      displayName: `Vendor`,
      type: FilterValueType.string,
      group: `General`,
    },
    {
      attribute: `trackingNumber`,
      displayName: `Tracking Number`,
      type: FilterValueType.string,
      group: `General`,
    },
    {
      attribute: `trackingNumberName`,
      displayName: `Tracking Number Name`,
      type: FilterValueType.string,
      group: `General`,
    },
    {
      attribute: `channel.keyword`,
      displayName: `Channel`,
      type: FilterValueType.asyncSelect,
      group: `Marketing`,
      nonEmpty: true,
      optionsSource: `/api/channels`,
      optionsMapper: (res) => {
        if (res.total > 0) {
          return res.hits.map(
            (entry) =>
              ({
                id: entry,
                value: entry,
                name: entry,
              }) as OptionType,
          )
        }
        return []
      },
    },
    ...(addMarketingColumns
      ? [
          {
            attribute: `utmSource`,
            displayName: `Source`,
            type: FilterValueType.stringArray,
            group: `Marketing`,
            optionsSource: `/api/activities/utmSource/values?inQueueCallTerm=true&defaultActivitiesTerm=true`,
          },
          {
            attribute: `utmMedium`,
            displayName: `Medium`,
            type: FilterValueType.stringArray,
            group: `Marketing`,
            optionsSource: `/api/activities/utmMedium/values?inQueueCallTerm=true&defaultActivitiesTerm=true`,
          },
          {
            attribute: `utmCampaign`,
            displayName: `Campaign`,
            type: FilterValueType.stringArray,
            group: `Marketing`,
            optionsSource: `/api/activities/utmCampaign/values?inQueueCallTerm=true&defaultActivitiesTerm=true`,
          },
          {
            attribute: `trackableCampaignId`,
            displayName: `Ad Campaign`,
            group: `Marketing`,
            type: FilterValueType.id,
            defaultValue: [`*`],
            nonEmpty: true,
            optionsSource: `/api/campaigns`,
            customComboOption: () => (
              <div className="flex items-center">
                <DollarCircle height={16} width={16} className="mr-1 text-on-color" style={{ fill: MONEY_ICON_FILL }} />
                <span>Ad Campaign</span>
              </div>
            ),
            optionsMapper: (res: Jsonify<SearchResponse<AdCampaign>>) => {
              if (res.total > 0) {
                return uniqBy(
                  res.hits
                    ?.sort((a, b) => {
                      if (a.type && b.type) return a.type?.localeCompare(b?.type)
                      return -1
                    })
                    ?.map(
                      (entry) =>
                        ({
                          id: entry.id,
                          value: entry.id,
                          name: entry.name,
                          meta: {
                            customComboOption: ({ selected }) => (
                              <CampaignComboOption
                                campaign={entry}
                                workspaceId={entry.workspaceId}
                                selected={selected}
                                type={entry.type!}
                              />
                            ),
                          },
                        }) as OptionType,
                    ),
                  `value`,
                )
              }
              return []
            },
          },

          {
            attribute: `utmContent`,
            displayName: `Content`,
            type: FilterValueType.string,
            group: `Marketing`,
          },
          {
            attribute: `utmKeywords`,
            displayName: `Keyword`,
            type: FilterValueType.string,
            group: `Marketing`,
          },
        ]
      : []),
    ...(showUtmFields
      ? [
          { attribute: `gclid`, displayName: `GCLID`, type: FilterValueType.string, group: `Marketing` },
          { attribute: `fbclid`, displayName: `FBCLID`, type: FilterValueType.string, group: `Marketing` },
          {
            attribute: `referringUrl`,
            displayName: `Referral URL`,
            type: FilterValueType.string,
            group: `Marketing`,
          },
        ]
      : []),
    {
      attribute: `subType`,
      displayName: `Form Name`,
      type: FilterValueType.string,
      group: `General`,
      subQuery: `type:Form`,
    },
  ])

  return filters?.map((filter) => ({
    ...filter,
    ...addDefaultFilterProperties(filter.attribute, preAppliedFilters),
  }))
}

/** Column IDs to exclude from AI Agent pages — formName only applies to Form activities */
export const AI_AGENT_EXCLUDED_COLUMN_IDS = new Set([`formName`])

/** Column IDs for AI Agent pages, in display order */
const AI_AGENT_COLUMN_ORDER = [
  `activityDate`,
  `phoneFromActivity`,
  `contacts`,
  `tags`,
  `locationId`,
  `owner`,
  `channel`,
]

/** Returns ordered columns for AI Agent pages, excluding irrelevant columns like formName */
export const aiAgentColumns = (
  addMarketingColumns: boolean,
  baseUrl: string | undefined,
  activityHandler: (row: ActivityJson) => void,
  onOwnerUpdate: (activityId: string, staffId?: any) => void,
  showUtmFields = true,
  onContactClick?: (contactId: string) => void,
): ColumnDef<ActivityJson, any>[] => {
  const allCols = defaultColumns(
    activityHandler,
    addMarketingColumns,
    onOwnerUpdate,
    baseUrl,
    showUtmFields,
    onContactClick,
  )
  const ordered = compact(AI_AGENT_COLUMN_ORDER.map((id) => allCols.find((col) => col.id === id)))
  // Append any remaining columns not in the order list (except removed ones)
  const remaining = allCols.filter(
    (col) => !AI_AGENT_COLUMN_ORDER.includes(col.id as string) && !AI_AGENT_EXCLUDED_COLUMN_IDS.has(col.id as string),
  )
  return [...ordered, ...remaining]
}

/** The AI Agent call lists' starting widths, as proportions: Tags gets the most room. */
export const AI_AGENT_COLUMN_WEIGHTS: Record<string, number> = {
  activityDate: 189,
  phoneFromActivity: 148,
  contacts: 202,
  tags: 518,
  locationId: 262,
  channel: 189,
}

/** The AI Agent call lists start without these columns: Staff and Caller ID. */
const AI_AGENT_HIDDEN_BY_DEFAULT = new Set([`owner`, `nameFromActivity`])

/** Turns off the columns an AI Agent call list starts without; a user's saved choice still wins. */
export function withAiAgentDefaultVisibility(columns: ColumnDef<ActivityJson, any>[]): ColumnDef<ActivityJson, any>[] {
  return columns.map((column) =>
    AI_AGENT_HIDDEN_BY_DEFAULT.has(column.id ?? ``)
      ? { ...column, meta: { ...column.meta, defaultVisible: false } }
      : column,
  )
}

/** Filterable columns for AI Agent pages — removes Activity Type and Form Name since all results are AI Calls */
export const getAiAgentFilterableColumns = (options: {
  comActivityTypes: string[]
  preAppliedFilters?: PredefinedFilter
  addMarketingColumns: boolean
  showUtmFields?: boolean
}): FilterableColumn[] => {
  return getFilterableColumns({
    ...options,
    addFollowUp: false,
  }).filter((col) => col.attribute !== `type` && col.attribute !== `description`)
}
